/**
 * Map overlay — Phase 3 DOM module.
 * Shows on the map scene:
 *   - Priest XP widget (top-right)
 *   - Map-buttons bar (Skills [S], Stats [I] toggle buttons)
 *   - CTA button «Start defense» at the bottom-center
 *
 * The actual skills/stats panels are mounted separately but share
 * the same mapButtonsContainer.
 */

import type { GameStateView } from "../../application/ports.js";
import { t, onLocaleChange } from "../../i18n/index.js";

// Default field for players who haven't selected anything yet.
const DEFAULT_FIELD_ID = "G1";

type MapView = NonNullable<GameStateView["map"]>;

function fieldStageNumber(fieldId: string): number {
  const match = /(\d+)$/.exec(fieldId);
  return match ? Number(match[1]) : 0;
}

function compareFieldProgress(a: string, b: string, fieldIds: readonly string[]): number {
  const stageDiff = fieldStageNumber(a) - fieldStageNumber(b);
  if (stageDiff !== 0) return stageDiff;
  const ai = fieldIds.indexOf(a);
  const bi = fieldIds.indexOf(b);
  if (ai < 0 && bi < 0) return a.localeCompare(b);
  if (ai < 0) return -1;
  if (bi < 0) return 1;
  return bi - ai;
}

export function chooseMapLaunchFieldId(map: MapView | undefined): string {
  if (!map) return DEFAULT_FIELD_ID;
  if (map.selectedFieldId) return map.selectedFieldId;

  const playable = new Set(map.playableFieldIds ?? map.fieldIds);
  const completed = new Set(map.mapProgress?.completedFieldIds ?? []);
  const unlocked = (map.mapProgress?.unlockedFieldIds?.length
    ? map.mapProgress.unlockedFieldIds
    : [map.startingFieldId ?? DEFAULT_FIELD_ID]
  ).filter((fieldId) => playable.has(fieldId));

  const open = unlocked.filter((fieldId) => !completed.has(fieldId));
  const candidates = open.length ? open : unlocked;
  if (!candidates.length) return map.startingFieldId ?? DEFAULT_FIELD_ID;

  return [...candidates].sort((a, b) => compareFieldProgress(b, a, map.fieldIds))[0];
}

export interface MapOverlayCallbacks {
  /** Called when the player clicks «Start defense» CTA. */
  onFieldLaunch?: (fieldId: string) => void;
  /** Called when the player clicks the editor shortcut (Shift+Click on field). */
  onFieldEdit?: (fieldId: string) => void;
}

export interface MapOverlay {
  /** The container element where skills/stats toggle buttons are appended. */
  mapButtonsContainer: HTMLElement;
  update(state: GameStateView): void;
  destroy(): void;
}

export function createMapOverlay(container: HTMLElement, cbs: MapOverlayCallbacks = {}): MapOverlay {
  // Wrapper — visible only on map scene
  const el = document.createElement("div");
  el.className = "hud-map-overlay";
  el.style.display = "none";
  container.appendChild(el);

  // Priest XP widget (top-right)
  const xpWidget = document.createElement("div");
  xpWidget.className = "hud-map-xp";
  el.appendChild(xpWidget);

  const xpTitle = document.createElement("div");
  xpTitle.className = "hud-map-xp__title";
  xpWidget.appendChild(xpTitle);

  const xpBarTrack = document.createElement("div");
  xpBarTrack.className = "hud-map-xp__bar-track";
  const xpBarFill = document.createElement("div");
  xpBarFill.className = "hud-map-xp__bar-fill";
  xpBarTrack.appendChild(xpBarFill);
  xpWidget.appendChild(xpBarTrack);

  const xpText = document.createElement("div");
  xpText.className = "hud-map-xp__text";
  xpWidget.appendChild(xpText);

  // Map buttons container (Skills and Stats buttons are appended by their modules)
  const mapButtonsContainer = document.createElement("div");
  mapButtonsContainer.className = "hud-map-buttons";
  el.appendChild(mapButtonsContainer);

  // CTA button — bottom-center of the parchment, always visible on map scene.
  const ctaBtn = document.createElement("button");
  ctaBtn.className = "hud-map-cta";
  ctaBtn.textContent = t("map.launchCta");
  el.appendChild(ctaBtn);

  let currentFieldId: string = DEFAULT_FIELD_ID;

  ctaBtn.addEventListener("click", () => {
    cbs.onFieldLaunch?.(currentFieldId);
  });

  // Keep button text in sync with locale changes.
  const unsubLocale = onLocaleChange(() => {
    ctaBtn.textContent = t("map.launchCta");
  });

  function update(state: GameStateView): void {
    const isMap = state.scene === "map";
    el.style.display = isMap ? "" : "none";
    if (!isMap) return;

    // XP widget hidden — priest info is now shown in the shared top bar
    xpWidget.style.display = "none";

    // Track the selected field; otherwise launch the farthest unlocked unbeaten field.
    currentFieldId = chooseMapLaunchFieldId(state.map);
  }

  function destroy(): void {
    unsubLocale();
    el.remove();
  }

  return { mapButtonsContainer, update, destroy };
}
